import { useGetList } from "react-admin";
import { ATTACHMENT_RECORD_SEGMENT, type AttachmentRecordType } from "./types";

/**
 * Presence + count for a record's first-class document attachments (#6505 /
 * ADR-086), record-type-parameterized so every DocumentsPanel host shares one
 * implementation (#6506 generalizes the policy-only `usePolicyHasAttachments`).
 *
 * Reads the attachment relation (`GET /{segment}/{id}/attachments`), NOT
 * `source_documents` (provenance, no longer written by the attachment path). The
 * show header uses this so the entry-point label reflects real attachments; a
 * view-only user must still see the entry point for a record whose only documents
 * are attachments.
 *
 * react-query dedupes concurrent callers with the same key, so the tab label and
 * the actions button share one request.
 */
export const useRecordHasAttachments = (recordType: AttachmentRecordType, recordId?: string) => {
  const segment = ATTACHMENT_RECORD_SEGMENT[recordType];
  const { data, total, isLoading, error } = useGetList(
    `${segment}/${recordId}/attachments`,
    { pagination: { page: 1, perPage: 1 } },
    { enabled: !!recordId }
  );
  // `unknown` = we cannot yet say whether attachments exist: the request is in
  // flight, or it failed. Callers must fail OPEN on this (show the entry point) —
  // a false "no attachments" would silently strip a user's only route to
  // documents they're entitled to see, and a masked 404 (a record the caller
  // can't reach, ADR-011) is indistinguishable from an empty relation.
  const count = total ?? data?.length ?? 0;
  return {
    hasAttachments: count > 0,
    count,
    isLoading,
    error,
    unknown: isLoading || !!error,
  };
};
